import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';

import { OgcParams } from '../../models/geo/ogcParams';
import { Layer } from '../../models/maps/layer';

import MapLayerControl from '../maps/MapLayerControl';
import MapSearchControl from '../maps/MapSearchControl';

import { ApiError } from '../../services/utils/apiError';

import { eventBus } from '../../services/event-emitter.service';
import { plannenService } from '../../services/plannen.service';
import { mapService } from '../../services/map.service';
import { planManagementService } from '../../services/plan-management.service';
import { assetsService } from '../../services/assets.service';
import { organisatieService } from '../../services/organisaties.service';
import { layerService } from '../../services/layer.service';
import { authService } from '../../services/auth.service';
import ErrorMessage from './ErrorMessage';

import { FeatureCollection } from 'geojson';
import * as atlas from 'azure-maps-control'; // Import the Azure Maps SDK
import "azure-maps-control/dist/atlas.min.css";
import { ListParams } from '../../models/listParams';


interface AzureMapProps {
  portfolioId?: string; // ID of the current portfolio
  portfolioActiveParticipant?: boolean; // Active participant in the portfolio
  selectedPlanId?: string | null; // Selected plan ID for highlighting
  onSelectPlan?: (planId: string | null) => void; // Callback when a plan is selected
}

const AzureMap: React.FC<AzureMapProps> = ({
  portfolioId,
  portfolioActiveParticipant,
  selectedPlanId,
  onSelectPlan
}) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  // const mapInstance = useRef<atlas.Map | null>(null);
  const searchTextRef = useRef<string | null>(null);
  const filterRef = useRef<string | null>(null);
  const [currentZoomLevel, setCurrentZoomLevel] = useState<number | null>(null);

  // Use a Map to store data sources for each discipline
  const dataSourceMapRef = useRef<Map<string, atlas.source.DataSource>>(new Map());
  const popupRef = useRef<atlas.Popup | null>(null); // Ref for popup
  const highlightedLayersRef = useRef<atlas.layer.Layer[]>([]); // Ref for highlighted layers
  const mapReadyRef = useRef<boolean>(false);
  const onSelectPlanRef = useRef(onSelectPlan); // Ref to keep onSelectPlan accessible in closures
  const pendingBboxRef = useRef<[number, number, number, number] | null>(null); // Ref to store bbox pending draw

  // Update ref on every render to always have the latest callback
  onSelectPlanRef.current = onSelectPlan;

  // State to hold layers
  const [activeLayers, setActiveLayers] = useState<Layer[]>([]);
  const [mapLoadError, setMapLoadError] = useState<boolean>(false);
  const [wmsLayerError, setWmsLayerError] = useState<string | null>(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState<boolean>(false);
  const genericMapErrorMessage = 'Er is een onbekende fout opgetreden bij het laden van een kaartlaag. Probeer het opnieuw of kies een andere laag.';
  const mapLoadErrorMessage = 'De kaart kon niet worden geladen. Controleer de internetverbinding of probeer de pagina te vernieuwen.';

  const activeLayersRef = useRef<Layer[]>([]);
  const mapErrorHandlerRef = useRef<(event: any) => void>();
  const activeWmsLayersRef = useRef<Map<string, atlas.layer.TileLayer>>(new Map());
  const activeAssetLayersRef = useRef<Map<string, {
    dataSource: atlas.source.DataSource;
    layers: atlas.layer.Layer[];
    clickHandlers: Array<(e: any) => void>;
    moveHandler: () => void;
  }>>(new Map());

  const escapeHtml = (value: string): string => {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const isFilledPropertyValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length > 0;
    return true;
  };

  const formatPropertyLabel = (key: string): string => {
    const spaced = key
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .trim();
    if (!spaced) return key;
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const formatPropertyValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.join(', ');
    return JSON.stringify(value);
  };

  const buildAssetPopupContent = (layerDisplayName: string, properties: Record<string, unknown>): string => {
    const rows = Object.entries(properties)
      .filter(([key, value]) => !key.toLowerCase().startsWith('infpl') && isFilledPropertyValue(value))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => {
        return `<label>${escapeHtml(formatPropertyLabel(key))}</label> ${escapeHtml(formatPropertyValue(value))} <br/>`;
      })
      .join('');

    return `
      <div class="popup-content popup-plan">
        <h5>${escapeHtml(layerDisplayName || 'Kaartlaag')}</h5>
        <div class="popup-data">
          ${rows || '<span>Geen eigenschappen gevonden</span>'}
        </div>
      </div>
    `;
  };

  const MIN_ZOOM_FOR_PLANS = 10;

  const handleZoomInOnPlanBbox = (bbox: [number, number, number, number]) => {
    if (!bbox) {
      // Trigger with current map bounds if no bbox is provided
      gePlannenOnChangeMap();
      return;
    }

    if (!mapService.isValidBbox(bbox)) {
      console.warn('Ongeldige bbox ontvangen voor zoomInOnPlanBbox:', bbox);
      return;
    }
    
    const bounds = mapService.convertBboxRDToLatLng(bbox, true) as number[];

    const map = mapService.getMapInstance();
    if (map) {
      let fallbackTimeoutId: number | undefined;

      const enforceMinimumPlanZoom = () => {
        const currentZoom = map.getCamera().zoom;
        if (currentZoom !== undefined && currentZoom < MIN_ZOOM_FOR_PLANS) {
          // Calculate center of the bounds to keep the view centered on the plans
          const centerLng = (bounds[0] + bounds[2]) / 2;
          const centerLat = (bounds[1] + bounds[3]) / 2;
          map.setCamera({
            center: [centerLng, centerLat],
            zoom: MIN_ZOOM_FOR_PLANS,
            duration: 1000,
          });
        }
      };

      let hasHandledZoomEnd = false;
      const handleZoomEnd = () => {
        if (hasHandledZoomEnd) return;
        hasHandledZoomEnd = true;

        map.events.remove('moveend', handleZoomEnd);
        map.events.remove('idle', handleZoomEnd);
        if (fallbackTimeoutId !== undefined) {
          window.clearTimeout(fallbackTimeoutId);
        }

        enforceMinimumPlanZoom();
      };

      // Register listeners before setCamera to avoid missing fast event cycles.
      map.events.add('moveend', handleZoomEnd);
      map.events.add('idle', handleZoomEnd);

      // Fallback for cases where no map movement event is emitted.
      fallbackTimeoutId = window.setTimeout(handleZoomEnd, 1300);

      map.setCamera({
        bounds: bounds,
        padding: 50, // Add padding for a better view
        duration: 1000, // Smooth animation
      });
    }
  };

  const setOgcFeatureFilterParams = (
    searchText: string, 
    filter: string
  ) => {
    searchTextRef.current = searchText;
    filterRef.current = filter;
  };

  const handleDrawPortfolioBbox = (bbox: [number, number, number, number]) => {
    pendingBboxRef.current = bbox;
    if (mapReadyRef.current) {
      mapService.drawPortfolioBbox(bbox);
      pendingBboxRef.current = null;
    }
  };

  useEffect(() => {
    activeLayersRef.current = activeLayers;
  }, [activeLayers]);

  // Keep onSelectPlan ref updated when prop changes
  useEffect(() => {
    onSelectPlanRef.current = onSelectPlan;
  }, [onSelectPlan]);

  const handleMapClickRef = useRef<(e: any) => void>();

  useEffect(() => {
    // Suppress the benign "ResizeObserver loop completed with undelivered notifications"
    // browser warning that Azure Maps triggers during zoom/pan/resize.
    const resizeObserverErrorHandler = (e: ErrorEvent) => {
      if (e.message?.includes('ResizeObserver')) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', resizeObserverErrorHandler);

    // Some browsers route ResizeObserver warnings through console.error instead.
    const originalConsoleError = console.error.bind(console);
    console.error = (...args: any[]) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) return;
      originalConsoleError(...args);
    };

    // Ensure ref is updated with current onSelectPlan callback BEFORE setting up handlers
    onSelectPlanRef.current = onSelectPlan;

    eventBus.on('zoomInOnPlanBbox', handleZoomInOnPlanBbox);
    eventBus.on('setOgcFilter', setOgcFeatureFilterParams);
    eventBus.on('drawPortfolioBbox', handleDrawPortfolioBbox);

    if (mapRef.current) {
      const initializeMapAsync = async () => {
        const map = await mapService.initializeMap(mapRef.current!);
        if (map) {
          const errorHandler = (event: any) => {
            const message: string = event?.message ?? event?.error?.message ?? '';
            const sourceId = event?.sourceId ?? event?.error?.sourceId ?? null;
            const layerId  = event?.layerId  ?? event?.error?.layerId  ?? null;
            const tileId   = event?.tileId   ?? event?.error?.tileId   ?? null;

            const details = {
              message: message || 'Onbekende Azure Maps fout',
              source: event?.source ?? event?.error?.source ?? null,
              sourceId,
              layerId,
              tileId,
              style: map.getStyle()?.style,
            };

            const logRuntimeError = (classification: string) => {
              console.error('Azure Maps runtime error', {
                classification,
                ...details,
              }, event);
            };

            // ResizeObserver loop notification is a benign browser warning, not a real error.
            if (message.includes('ResizeObserver')) {
              logRuntimeError('resize-observer');
              setWmsLayerError(genericMapErrorMessage);
              return;
            }

            // Tile fetch network errors (Failed to fetch / connection reset) are transient
            // and not actionable — suppress them to keep the console clean.
            if (message.includes('Failed to fetch') || message.includes('ERR_CONNECTION')) {
              logRuntimeError('tile-fetch-network');
              setWmsLayerError('Een kaartlaag kon niet worden geladen: netwerkfout bij het ophalen van kaarttegels. Controleer je verbinding of probeer het opnieuw.');
              return;
            }

            // WMS tiles that return a non-image response (403, XML error body, etc.) trigger
            // this browser-level decode error — show it in the Snackbar and suppress the console noise.
            if (message.includes('source image could not be decoded')) {
              logRuntimeError('tile-image-decode');
              setWmsLayerError('Een kaartlaag kon niet worden geladen: de server gaf geen geldige afbeelding terug. Controleer de WMS-url of de toegangsrechten.');
              return;
            }

            logRuntimeError('unknown');
            setWmsLayerError(genericMapErrorMessage);

            // Fatale fout: stijl kon niet geladen worden
            if (!sourceId && !layerId && !tileId) {
              setMapLoadError(true);
            }
          };
          mapErrorHandlerRef.current = errorHandler;
          map.events.add('error', errorHandler);

          map.events.add('ready', async () => {
            mapReadyRef.current = true;
            
            // Draw pending bbox if it was received before map was ready
            if (pendingBboxRef.current) {
              mapService.drawPortfolioBbox(pendingBboxRef.current);
              pendingBboxRef.current = null;
            }
            
            await plannenService.getDisciplines();
            if (authService.isAdmin()) {
              await organisatieService.getAllOrganisatiesCached();
            }

            // Guard: component may have unmounted during the await (e.g. React StrictMode)
            if (mapService.getMapInstance() !== map) return;

            // Get both public WMS and asset layers and store in state.
            const [publicLayers, assetLayerResult] = await Promise.all([
              planManagementService.getActiveLayers(),
              assetsService.getAssetsLayers({
                _offset: 0,
                _limit: 500,
              } as ListParams),
            ]);

            // Guard again after second await
            if (mapService.getMapInstance() !== map) return;

            const mappedAssetLayers: Layer[] = (assetLayerResult?.items ?? []).map((assetLayer: any) => {
              const organizationId = assetLayer.sourceOrganizationId ?? '';
              const organizationName = organizationId
                ? organisatieService.getOrganisatieNaam(organizationId)
                : undefined;

              return {
                id: assetLayer.id ?? '',
                organizationId,
                url: '',
                sourceName: assetLayer.sourceName ?? organizationName ?? 'Overig',
                layerDisplayName: assetLayer.layerDisplayName ?? '',
                description: assetLayer.description,
                assetCount: assetLayer.assetCount,
                type: 'geopackage' as const,
                selected: false,
              } as Layer;
            }).filter((layer: Layer) => !!layer.id && !!layer.layerDisplayName);

            const mappedPublicLayers = publicLayers.map((layer: Layer) => ({
              ...layer,
              selected: layer.selected ?? false,
            }));

            setActiveLayers([...mappedPublicLayers, ...mappedAssetLayers]);

            layerService.initializeLayerAndEvents(map, dataSourceMapRef.current, popupRef, portfolioId, portfolioActiveParticipant, onSelectPlanRef);
            // Listen for bounding box changes (on zoom, pan, etc.)
            map.events.add('moveend', gePlannenOnChangeMap);
            gePlannenOnChangeMap(); // Trigger once
          });

          const handler = (e: any) => {
            const planClicked = layerService.handleMapClick(e, map, popupRef, dataSourceMapRef.current, onSelectPlanRef, portfolioId, portfolioActiveParticipant);

            // No plan shape at the clicked position: try GetFeatureInfo on active WMS layers.
            if (!planClicked) {
              const selectedWmsLayers = activeLayersRef.current.filter(
                l => l.selected && (l.type === 'wms' || l.type === 'ogc_tiles') && !!l.url
              );
              if (selectedWmsLayers.length > 0) {
                layerService.showFeatureInfoPopup(e, map, popupRef, selectedWmsLayers)
                  .catch(err => console.error('GetFeatureInfo popup error:', err));
              }
            }
          };
        
          handleMapClickRef.current = handler;
        
          map.events.add('click', handler);

          mapService.setMapInstance(map);
        }
      };
      
      initializeMapAsync();
    }

    // Cleanup map instance on unmount
    return () => {
      window.removeEventListener('error', resizeObserverErrorHandler);
      console.error = originalConsoleError;
      mapReadyRef.current = false;
      pendingBboxRef.current = null;
      // Cleanup to prevent memory leaks
      eventBus.off('zoomInOnPlanBbox', handleZoomInOnPlanBbox);
      eventBus.off('setOgcFilter', setOgcFeatureFilterParams);
      eventBus.off('drawPortfolioBbox', handleDrawPortfolioBbox);

      const map = mapService.getMapInstance();
      if (map) {
        mapService.clearPortfolioBbox();

        if (handleMapClickRef.current) {
          map.events.remove('click', handleMapClickRef.current);
        }

        if (mapErrorHandlerRef.current) {
          map.events.remove('error', mapErrorHandlerRef.current);
        }

        map.events.remove('moveend', gePlannenOnChangeMap);
        map.dispose(); // Ensure the map is destroyed properly
        mapService.setMapInstance(null);  // Set map instance to null after cleanup
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle selectedPlanId changes - update highlighted layers
  useEffect(() => {
    const map = mapService.getMapInstance();
    if (!map) return;

    // Remove old highlighted layers
    highlightedLayersRef.current.forEach(layer => {
      map.layers.remove(layer);
    });
    highlightedLayersRef.current = [];

    if (selectedPlanId) {
      const dataSource = dataSourceMapRef.current.get('Default');
      if (dataSource) {
        // Create outer glow layer (thickest stroke, subtle black)
        const glowLineLayer = new atlas.layer.LineLayer(dataSource, undefined, {
          strokeColor: 'rgba(0, 0, 0, 0.25)',
          strokeWidth: 4,
          filter: ['==', ['get', 'infplId'], selectedPlanId]
        });

        // Create highlighted polygon layer (light fill)
        const highlightedPolygonLayer = new atlas.layer.PolygonLayer(dataSource, undefined, {
          fillColor: 'rgba(0, 0, 0, 0.5)',
          fillOpacity: 0.5,
          filter: ['==', ['get', 'infplId'], selectedPlanId]
        });

        // Create highlighted line layer (main border - black, thick)
        const highlightedLineLayer = new atlas.layer.LineLayer(dataSource, undefined, {
          strokeColor: 'rgb(0, 0, 0)',
          strokeWidth: 2,
          filter: ['==', ['get', 'infplId'], selectedPlanId]
        });

        // Add layers to map (glow first, then polygon, then line on top)
        map.layers.add(glowLineLayer, 'labels');
        map.layers.add(highlightedPolygonLayer, 'labels');
        map.layers.add(highlightedLineLayer, 'labels');

        highlightedLayersRef.current = [glowLineLayer, highlightedPolygonLayer, highlightedLineLayer];
      }
    }
  }, [selectedPlanId]);


  // Function to update polygons based on the current bbox
  const gePlannenOnChangeMap = async () => {
    const map = mapService.getMapInstance();
    if (map) { // && dataSourceRef.current
      const zoomLevel = map.getCamera().zoom;

      if (zoomLevel) {
        setCurrentZoomLevel(zoomLevel);
      }

      // console.log('Zoom:', zoomLevel);
      if (zoomLevel && zoomLevel >= MIN_ZOOM_FOR_PLANS) {
        const bbox = map.getCamera().bounds; // Get current bounding box (bbox)
        if (bbox) {
          // Draw bbox on the map for testing
          // drawBbox(map, bbox);

          // console.log('Current BBox:', bbox);
          // console.log('zoom:', zoomLevel);

          // Convert bbox coordinates from EPSG:4326 to EPSG:28992
          const bboxString = mapService.convertBboxLatLngToRD(bbox) as string;
          // console.log(`bboxString (RD): ${bboxString}`);

          // const [west, south] = proj4("EPSG:4326", "EPSG:28992", [bbox[0], bbox[1]]);
          // const [east, north] = proj4("EPSG:4326", "EPSG:28992", [bbox[2], bbox[3]]);

          const ogcParams = new OgcParams();
          ogcParams.offset = 0;
          ogcParams.limit = 1000;
          ogcParams.bbox = bboxString;
          if (searchTextRef.current) {
            ogcParams.searchText = searchTextRef.current;
          }
          if (filterRef.current) {
            ogcParams.filter = filterRef.current;
          }

          ogcParams.properties = 'infplId,planId,planNaam,planStartDatum,planEindDatum,planStatus,discipline,infplOrganizationName' // 'planNaam,organisationName,status,discipline,infplId';
          
          // Fetch polygons based on bbox. For now, we will create a static polygon inside the bbox
          // Get geoJSON data from the API based on the bbox
          setIsLoadingPlans(true);
          let data: FeatureCollection | null = null;
          try {
            data = await plannenService.getPlannenOgcFeatures(ogcParams);
          } catch (err) {
            console.warn('Plannen ophalen mislukt (filter fout?):', err);
            clearDataSource();
            return;
          } finally {
            setIsLoadingPlans(false);
          }

          // Guard: component may have unmounted during the await
          if (mapService.getMapInstance() !== map) return;

          // If you receive GeoJSON, add it to the map
          if (data && data.features) {
            // if (data.features.length > 0) {
            //   //console.log('items found:', data);
            // }

            clearDataSource();
            layerService.createPolygonsOnMap(data, dataSourceMapRef.current);
          }
        }
      }
      else {
        // console.log(`Zoomlevel ${zoomLevel} is to low to load plans on map. Minimum zoom level is 11.`);
        clearDataSource();

        // if (this.doesExist(this.renderedPolygonIds)) {
        //   this.renderedPolygonIds.clear();
        // }
        // if (this.doesExist(this.shapesForBounds)) {
        //   this.shapesForBounds.clear();
        // }
      }
    }
  };


  // EPSG:28992 version implementation

  const clearDataSource = () => {
    const dataSourcePolygons = dataSourceMapRef.current.get('Default');
    if (dataSourcePolygons) {
      dataSourcePolygons.clear();
    }
  };

  const onToggleActiveLayerGroup = (groupLayers: Layer[], checked: boolean) => {
    groupLayers.forEach(layer => {
      if ((layer.selected ?? false) !== checked) {
        onToggleActiveLayer(layer);
      }
    });
  };

  const onToggleActiveLayer = async (layer: Layer) => {
    if (layer.type === 'geopackage') {
      await onToggleAssetLayer(layer);
      return;
    }
    await onToggleWmsLayer(layer);
  };

  const onToggleWmsLayer = async (layer: Layer) => {
    const map = mapService.getMapInstance();
    if (!map) return;
  
    const existingLayer = activeWmsLayersRef.current.get(layer.id);
  
    if (existingLayer) {
      map.layers.remove(existingLayer);
      activeWmsLayersRef.current.delete(layer.id);
      setActiveLayers(prev => prev.map(l => l.id === layer.id ? { ...l, selected: false } : l));
      return;
    }

    // Probe the WMS endpoint before adding the tile layer
    try {
      const [baseUrl] = layer.url.split('?');
      const probeUrl = `${baseUrl}?service=WMS&request=GetCapabilities`;
      const response = await fetch(probeUrl);

      if (response.status === 403) {
        console.error('WMS probe error', {
          layerId: layer.id,
          layerName: layer.layerDisplayName,
          status: 403,
          probeUrl,
          reason: 'access-denied',
        });
        setWmsLayerError(`Kaartlaag "${layer.layerDisplayName}" kon niet worden geladen: toegang geweigerd (403).`);
        return;
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('xml') || contentType.includes('text')) {
        const text = await response.text();
        if (text.includes('ServiceExceptionReport') || text.includes('ServiceException')) {
          console.error('WMS probe error', {
            layerId: layer.id,
            layerName: layer.layerDisplayName,
            status: response.status,
            probeUrl,
            reason: 'service-exception',
          });
          setWmsLayerError(`Kaartlaag "${layer.layerDisplayName}" kon niet worden geladen: de WMS-service heeft een fout teruggegeven.`);
          return;
        }
        if (!text.includes('WMS_Capabilities') && !text.includes('WMT_MS_Capabilities')) {
          console.error('WMS probe error', {
            layerId: layer.id,
            layerName: layer.layerDisplayName,
            status: response.status,
            probeUrl,
            reason: 'invalid-capabilities-response',
          });
          setWmsLayerError(`Kaartlaag "${layer.layerDisplayName}" kon niet worden geladen: geen geldige WMS-data ontvangen.`);
          return;
        }
      }
    } catch (error) {
      console.error('WMS probe error', {
        layerId: layer.id,
        layerName: layer.layerDisplayName,
        reason: 'probe-fetch-failed',
        error,
      });
      setWmsLayerError(`Kaartlaag "${layer.layerDisplayName}" kon niet worden geladen: de server is niet bereikbaar.`);
      return;
    }

    const wmsUrl = `${layer.url}&bbox={bbox-epsg-3857}`;
    const tileLayer = new atlas.layer.TileLayer({ tileUrl: wmsUrl, tileSize: 256, opacity: 0.9 });
    map.layers.add(tileLayer, "labels");
    activeWmsLayersRef.current.set(layer.id, tileLayer);
    setActiveLayers(prev => prev.map(l => l.id === layer.id ? { ...l, selected: true } : l));
  };

  const convertGeometryCoordinatesToWgs84 = (coordinates: any): any => {
    if (!Array.isArray(coordinates)) {
      return coordinates;
    }

    if (coordinates.length >= 2 && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
      return mapService.convertRDToWGS84([coordinates[0], coordinates[1]]);
    }
    return coordinates.map(convertGeometryCoordinatesToWgs84);
  };

  const refreshAssetLayerFeatures = async (layer: Layer, dataSource: atlas.source.DataSource, map: atlas.Map) => {
    const bounds = map.getCamera().bounds;
    const ogcParams = new OgcParams();
    ogcParams.limit = 1000;
    if (Array.isArray(bounds) && bounds.length === 4) {
      ogcParams.bbox = mapService.convertBboxLatLngToRD(bounds) as string;
    }

    let featureCollection: FeatureCollection;
    try {
      featureCollection = await assetsService.getAssetLayerOgcFeatures(layer.id, ogcParams);
    } catch (error) {
      if (error instanceof ApiError && error.isNotFound()) {
        console.warn(`Asset layer niet gevonden: ${layer.layerDisplayName} (${layer.id}).`);
        return;
      }
      console.error('Fout bij ophalen van asset layer OGC features:', error);
      return;
    }

    const crsName = (featureCollection as any)?.crs?.properties?.name as string | undefined;
    const shouldConvertFromRd = !!crsName && crsName.toUpperCase().includes('28992');

    // const dataSource = new atlas.source.DataSource(`asset-layer-${layer.id}`, { buffer: 0 });
    // map.sources.add(dataSource);
    dataSource.clear();

    (featureCollection?.features ?? []).forEach((feature: any) => {
      const geometry = feature?.geometry;
      if (!geometry) return;

      const geometryCoordinates = shouldConvertFromRd
        ? convertGeometryCoordinatesToWgs84(geometry.coordinates)
        : geometry.coordinates;

      let atlasGeometry: atlas.data.Geometry | null = null;
      switch (geometry.type) {
        case 'Polygon': atlasGeometry = new atlas.data.Polygon(geometryCoordinates); break;
        case 'MultiPolygon': atlasGeometry = new atlas.data.MultiPolygon(geometryCoordinates); break;
        case 'LineString': atlasGeometry = new atlas.data.LineString(geometryCoordinates); break;
        case 'MultiLineString': atlasGeometry = new atlas.data.MultiLineString(geometryCoordinates); break;
        case 'Point': atlasGeometry = new atlas.data.Point(geometryCoordinates); break;
        case 'MultiPoint': atlasGeometry = new atlas.data.MultiPoint(geometryCoordinates); break;
        default: atlasGeometry = null;
      }
      if (!atlasGeometry) return;

      dataSource.add(new atlas.Shape(
        new atlas.data.Feature(atlasGeometry, {
          ...feature.properties,
          layerName: layer.layerDisplayName,
        })
      ));
    });
  };

  const onToggleAssetLayer = async (layer: Layer) => {
    const map = mapService.getMapInstance();
    if (!map || !layer.id) return;

    const existingAssetLayer = activeAssetLayersRef.current.get(layer.id);

    if (existingAssetLayer) {
      map.events.remove('moveend', existingAssetLayer.moveHandler);

      existingAssetLayer.layers.forEach((mapLayer, index) => {
        const clickHandler = existingAssetLayer.clickHandlers[index];
        if (clickHandler) {
          map.events.remove('click', mapLayer, clickHandler);
        }
      });

      existingAssetLayer.layers.forEach(mapLayer => {
        map.layers.remove(mapLayer);
      });

      map.sources.remove(existingAssetLayer.dataSource);
      activeAssetLayersRef.current.delete(layer.id);

      setActiveLayers(prev =>
        prev.map(l =>
          l.id === layer.id ? { ...l, selected: false } : l
        )
      );

      return;
    }

    const dataSource = new atlas.source.DataSource(`geopackage-layer-${layer.id}`);
    map.sources.add(dataSource);

    await refreshAssetLayerFeatures(layer, dataSource, map);

    const polygonLayer = new atlas.layer.PolygonLayer(dataSource, undefined, {
      fillColor: 'rgba(23, 125, 220, 0.25)',
      strokeColor: 'rgba(23, 125, 220, 0.8)',
      strokeWidth: 1,
      filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
    });
    const lineLayer = new atlas.layer.LineLayer(dataSource, undefined, {
      strokeColor: 'rgba(23, 125, 220, 0.85)',
      strokeWidth: 2,
      filter: ['any', ['==', ['geometry-type'], 'LineString'], ['==', ['geometry-type'], 'MultiLineString']],
    });
    const pointLayer = new atlas.layer.BubbleLayer(dataSource, undefined, {
      color: 'rgba(23, 125, 220, 0.85)',
      radius: 5,
      strokeColor: 'rgba(255, 255, 255, 0.9)',
      strokeWidth: 1,
      filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']],
    });

    const renderedAssetLayers: atlas.layer.Layer[] = [polygonLayer, lineLayer, pointLayer];
    map.layers.add(renderedAssetLayers, 'labels');

    const clickHandlers = renderedAssetLayers.map((mapLayer) => {
      const clickHandler = (e: any) => {
        if (!e?.shapes?.length) return;
        const shape = e.shapes[0];
        let properties: Record<string, unknown> | null = null;
        if (shape instanceof atlas.Shape) {
          properties = shape.getProperties() as Record<string, unknown>;
        } else if (shape instanceof atlas.data.Feature) {
          properties = (shape.properties ?? null) as Record<string, unknown> | null;
        }
        if (!properties || Object.keys(properties).length === 0) return;

        if (!popupRef.current) {
          popupRef.current = new atlas.Popup({ pixelOffset: [0, -20], closeButton: true });
        }
        popupRef.current.setOptions({
          content: buildAssetPopupContent(layer.layerDisplayName, properties),
          position: e.position,
        });
        popupRef.current.open(map);
      };
      map.events.add('click', mapLayer, clickHandler);
      return clickHandler;
    });

    // Optional: auto-zoom naar de kaartlaag na renderen.
    // Laat dit voorlopig uitgeschakeld; uncomment om direct te activeren.
    // const firstBbox = (featureCollection?.features ?? []).find(f => Array.isArray(f?.bbox) && f.bbox.length === 4)?.bbox as number[] | undefined;
    // if (firstBbox) {
    //   const zoomBounds = shouldConvertFromRd
    //     ? mapService.convertBboxRDToLatLng(firstBbox, true) as number[]
    //     : firstBbox;
    //
    //   map.setCamera({
    //     bounds: zoomBounds,
    //     padding: 50,
    //     duration: 900,
    //   });
    // }

    const moveHandler = () => { refreshAssetLayerFeatures(layer, dataSource, map); };
    map.events.add('moveend', moveHandler);

    activeAssetLayersRef.current.set(layer.id, {
      dataSource,
      layers: renderedAssetLayers,
      clickHandlers,
      moveHandler,
    });

    setActiveLayers(prev =>
      prev.map(l =>
        l.id === layer.id ? { ...l, selected: true } : l
      )
    );
  };

  return (
    <div className="azure-map-wrapper">
      <div ref={mapRef} id="mapContainer" className="azure-map" />

      {mapLoadError && (
        <ErrorMessage
          message={mapLoadErrorMessage}
          autoHideDuration={6000}
          onClose={() => setMapLoadError(false)}
        />
      )}

      {wmsLayerError && (
        <ErrorMessage
          message={wmsLayerError}
          severity="warning"
          onClose={() => setWmsLayerError(null)}
        />
      )}
      
      <MapSearchControl />

      <MapLayerControl
        layers={activeLayers}
        onToggleLayer={onToggleActiveLayer}
        onToggleGroup={onToggleActiveLayerGroup}
      />

      {currentZoomLevel && currentZoomLevel < MIN_ZOOM_FOR_PLANS && (
        <div className="map-loading-indicator">
          <span className="material-icons" style={{ fontSize: 20 }}>zoom_in</span>
          <span>Zoom verder in om plannen te tonen</span>
        </div>
      )}

      {isLoadingPlans && (
        <div className="map-loading-indicator">
          <CircularProgress size={16} thickness={4} />
          <span>Plannen worden geladen</span>
        </div>
      )}
    </div>
  );
};

export default AzureMap;